import { Fragment } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CustomCheckbox,
  DateTimeText,
  GeneralTableBox,
  HeadTableCell,
  MaterialArticleChips,
  NoDataSkeleton,
  RowTableCell,
  SortableHeadTableCell,
  TableBodySkeleton,
} from '../../../common/components';
import {
  CONSOLIDATED_SUPPLY_TEXTS as PURCHASE_TEXTS,
} from '../../supply-orders/constants/consolidatedSupplyTexts';
import InternalOrderExpandButton from './InternalOrderExpandButton';
import InternalOrderExpandedMaterials from './InternalOrderExpandedMaterials';
import InternalOrderMobileField from './InternalOrderMobileField';
import InternalOrderStatusChip from './InternalOrderStatusChip';
import InternalOrderViewButton from './InternalOrderViewButton';
import {
  INTERNAL_ORDERS_TABLE_TEXTS as TEXTS,
  REQUESTED_ARTICLES_TEXTS,
} from '../constants/internalOrdersConstants';

const textSx = { textAlign: 'left' };
const MIN_TABLE_WIDTH = 1500;

const COLUMNS = [
  { label: TEXTS.number, sortKey: 'order_number', sx: { ...textSx, width: 140 } },
  { label: TEXTS.neededAt, sortKey: 'requested_at', sx: { ...textSx, width: 110 } },
  { label: TEXTS.warehouse, sortKey: 'warehouse', sx: { ...textSx, width: 190 } },
  { label: TEXTS.project, sortKey: 'project', sx: textSx },
  { label: REQUESTED_ARTICLES_TEXTS.column, sx: { ...textSx, width: 400 } },
  { label: TEXTS.createdBy, sortKey: 'created_by', sx: { ...textSx, width: 160 } },
  { label: TEXTS.status, sortKey: 'status', sx: { width: 190 } },
  { label: TEXTS.actions, sx: { width: 90 } },
];

const MOBILE_FIELDS = [
  { label: TEXTS.warehouse, getValue: (order) => order.warehouse_label || TEXTS.noWarehouse },
  { label: TEXTS.project, getValue: (order) => order.project_label || TEXTS.noProject },
];

/**
 * Tabla de pedidos técnicos (tarjetas en mobile) con materiales desplegables por fila.
 * Los encabezados de datos ordenan en el servidor (asc, desc, sin orden).
 * @param {Object} props
 * @param {{key: string, direction: string}|null} props.sort Orden vigente.
 * @param {Function} props.onSort Recibe la clave de la columna clickeada.
 * @returns {JSX.Element}
 */
const InternalOrdersDataTable = ({
  expandedId,
  expandedQuery,
  onExpand,
  data,
  selectedIds = [],
  onToggleSelection,
  onTogglePage,
  canSelect = () => false,
  isLoading,
  actionsDisabled = false,
  onView,
  sort = null,
  onSort,
  LoadingComponent = TableBodySkeleton,
}) => {
  const theme = useTheme();
  const pageIds = (data || []).filter(canSelect).map((row) => row.uuid);
  const selectedOnPage = pageIds.filter((id) => selectedIds.includes(id)).length;
  const columnsCount = COLUMNS.length + (onToggleSelection ? 1 : 0);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (isMobile) {
    return (
      <GeneralTableBox>
        {isLoading ? (
          <TableBodySkeleton variant="box" />
        ) : data && data.length > 0 ? (
          <Box sx={{ p: 2 }}>
            {data.map((order, index) => (
              <Box
                key={order.uuid || index}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                  p: 2,
                  mb: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  {onToggleSelection && (
                    <Tooltip
                      title={
                        canSelect(order)
                          ? PURCHASE_TEXTS.select
                          : order.purchase_ineligible_reason || PURCHASE_TEXTS.ineligible
                      }
                    >
                      <Box component="span" sx={{ display: 'inline-flex' }}>
                        <CustomCheckbox
                          size="small"
                          checked={selectedIds.includes(order.uuid) && canSelect(order)}
                          disabled={!canSelect(order) || actionsDisabled}
                          onChange={() => onToggleSelection(order.uuid)}
                          inputProps={{ 'aria-label': PURCHASE_TEXTS.select }}
                        />
                      </Box>
                    </Tooltip>
                  )}
                  <InternalOrderExpandButton
                    expanded={expandedId === order.uuid}
                    onClick={() => onExpand(order.uuid)}
                  />
                  <Typography variant="subtitle1" fontWeight="bold">
                    {TEXTS.orderTitle(order.number)}
                  </Typography>
                </Box>
                {expandedId === order.uuid && (
                  <InternalOrderExpandedMaterials query={expandedQuery} />
                )}
                {MOBILE_FIELDS.map((field) => (
                  <InternalOrderMobileField key={field.label} label={field.label}>
                    <Typography variant="body2">{field.getValue(order)}</Typography>
                  </InternalOrderMobileField>
                ))}
                <InternalOrderMobileField label={REQUESTED_ARTICLES_TEXTS.column}>
                  <MaterialArticleChips
                    articles={order.requested_articles}
                    quantityKey="requested_quantity"
                  />
                </InternalOrderMobileField>
                <InternalOrderMobileField label={TEXTS.createdBy}>
                  <Typography variant="body2">{order.created_by_label || '-'}</Typography>
                </InternalOrderMobileField>
                <InternalOrderMobileField label={TEXTS.neededAt}>
                  <Typography variant="body2">{order.requested_at_label || '-'}</Typography>
                </InternalOrderMobileField>
                <InternalOrderMobileField label={TEXTS.status}>
                  <Box sx={{ mt: 0.5 }}>
                    <InternalOrderStatusChip status={order.status} label={order.status_label} />
                  </Box>
                </InternalOrderMobileField>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <InternalOrderViewButton
                    onClick={() => onView(order)}
                    disabled={actionsDisabled}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <NoDataSkeleton colSpan={1} message={TEXTS.empty} />
        )}
      </GeneralTableBox>
    );
  }

  return (
    <GeneralTableBox fixedLayout minTableWidth={MIN_TABLE_WIDTH}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {onToggleSelection && (
              <HeadTableCell sx={{ width: 48, px: 0 }}>
                <Tooltip
                  title={
                    pageIds.length ? PURCHASE_TEXTS.selectPage : PURCHASE_TEXTS.noEligibleOrders
                  }
                >
                  <Box component="span" sx={{ display: 'inline-flex' }}>
                    <CustomCheckbox
                      size="small"
                      checked={pageIds.length > 0 && selectedOnPage === pageIds.length}
                      indeterminate={selectedOnPage > 0 && selectedOnPage < pageIds.length}
                      disabled={!pageIds.length || actionsDisabled}
                      onChange={onTogglePage}
                      sx={{ '& .MuiSvgIcon-root': { borderColor: 'common.white' } }}
                      inputProps={{ 'aria-label': PURCHASE_TEXTS.selectPage }}
                    />
                  </Box>
                </Tooltip>
              </HeadTableCell>
            )}
            {COLUMNS.map((column) => (
              <SortableHeadTableCell
                key={column.label}
                sortKey={column.sortKey}
                sort={sort}
                onSort={onSort}
                sx={column.sx}
              >
                {column.label}
              </SortableHeadTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <LoadingComponent />
          ) : data && data.length > 0 ? (
            data.map((order, index) => (
              <Fragment key={order.uuid || index}>
                <TableRow hover>
                  {onToggleSelection && (
                    <RowTableCell sx={{ px: 0 }}>
                      <Tooltip
                        title={
                          canSelect(order)
                            ? PURCHASE_TEXTS.select
                            : order.purchase_ineligible_reason || PURCHASE_TEXTS.ineligible
                        }
                      >
                        <Box component="span" sx={{ display: 'inline-flex' }}>
                          <CustomCheckbox
                            size="small"
                            checked={selectedIds.includes(order.uuid) && canSelect(order)}
                            disabled={!canSelect(order) || actionsDisabled}
                            onChange={() => onToggleSelection(order.uuid)}
                            inputProps={{ 'aria-label': PURCHASE_TEXTS.select }}
                          />
                        </Box>
                      </Tooltip>
                    </RowTableCell>
                  )}
                  <RowTableCell sx={{ ...textSx, py: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <InternalOrderExpandButton
                        expanded={expandedId === order.uuid}
                        onClick={() => onExpand(order.uuid)}
                      />
                      {order.number}
                    </Box>
                  </RowTableCell>
                  <RowTableCell sx={textSx}>
                    <DateTimeText value={order.requested_at || order.created_at} />
                  </RowTableCell>
                  <RowTableCell sx={textSx} title={order.warehouse_label}>
                    {order.warehouse_label || TEXTS.noWarehouse}
                  </RowTableCell>
                  <RowTableCell sx={textSx} title={order.project_label}>
                    {order.project_label || TEXTS.noProject}
                  </RowTableCell>
                  <RowTableCell sx={{ ...textSx, whiteSpace: 'normal' }}>
                    <MaterialArticleChips
                      articles={order.requested_articles}
                      quantityKey="requested_quantity"
                    />
                  </RowTableCell>
                  <RowTableCell sx={textSx} title={order.created_by_label}>
                    {order.created_by_label || '-'}
                  </RowTableCell>
                  <RowTableCell>
                    <InternalOrderStatusChip status={order.status} label={order.status_label} />
                  </RowTableCell>
                  <RowTableCell>
                    <InternalOrderViewButton
                      onClick={() => onView(order)}
                      disabled={actionsDisabled}
                    />
                  </RowTableCell>
                </TableRow>
                {expandedId === order.uuid && (
                  <TableRow>
                    <RowTableCell colSpan={columnsCount} sx={{ p: 0, whiteSpace: 'normal' }}>
                      <InternalOrderExpandedMaterials query={expandedQuery} />
                    </RowTableCell>
                  </TableRow>
                )}
              </Fragment>
            ))
          ) : (
            <NoDataSkeleton colSpan={columnsCount} message={TEXTS.empty} />
          )}
        </TableBody>
      </Table>
    </GeneralTableBox>
  );
};

export default InternalOrdersDataTable;
